import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CircleDashed, Loader2, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { ConfirmButton } from "@/components/admin/ConfirmButton";

const Mark = ({ ok }) => (ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> : ok === false ? <XCircle className="h-4 w-4 shrink-0 text-red-400" /> : <CircleDashed className="h-4 w-4 shrink-0 text-neutral-500" />);

export const ReviewBadge = ({ status, testId }) => {
  const { t } = useI18n();
  const pending = status === "pending";
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${pending ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/15 text-emerald-300"}`} data-testid={testId}>{t(pending ? "review.pending" : "review.published")}</span>;
};

const Checklist = ({ checks }) => {
  const { t } = useI18n();
  return (
    <ul className="grid gap-1.5 sm:grid-cols-2" data-testid="review-checklist">
      {checks.map((c) => (
        <li key={c.id} className="flex min-w-0 items-start gap-2 text-xs" data-testid={`review-check-${c.id}`}>
          <Mark ok={c.ok} />
          <span className="min-w-0 break-words [overflow-wrap:anywhere]"><span className="text-white">{t(`review.c.${c.id}`)}</span>{c.detail && <span className="text-neutral-400"> — {c.detail}</span>}</span>
        </li>
      ))}
    </ul>
  );
};

const PushLine = ({ push }) => {
  const { t, fmtDate } = useI18n();
  if (!push) return <p className="text-xs text-neutral-400" data-testid="review-push">{t("review.noPush")}</p>;
  const key = push.status === "simulated" ? "review.pushSimulated" : push.status === "done" ? "review.pushDone" : "review.pushQueued";
  return <p className="break-words text-xs text-neutral-300" data-testid="review-push">{t(key, { date: fmtDate(push.done_at || push.created_at), sent: push.sent, failed: push.failed, removed: push.removed, clicks: push.clicks, n: push.simulated ?? 0 })}</p>;
};

export const ReviewPanel = ({ appId, onChanged }) => {
  const { t, fmtDate } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/admin/apps/${appId}/review`).then(({ data }) => setS(data)).catch(() => {}), [appId]);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (kind, req) => {
    setBusy(kind);
    try {
      const { data } = await req();
      setS(data);
      onChanged?.();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy("");
    }
  };
  if (!s) return null;
  const pending = s.status === "pending";
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="review-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex min-w-0 items-center gap-2 break-words font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg"><ShieldCheck className="h-5 w-5 shrink-0" /> {t("review.title")}</h2>
        <ReviewBadge status={s.status} testId="review-status-badge" />
        {s.version && <span className="text-xs text-neutral-400" data-testid="review-version">v{s.version.version}{s.version.version_code ? ` (${s.version.version_code})` : ""}</span>}
      </div>
      {s.grandfathered && !pending && <p className="text-xs text-neutral-400" data-testid="review-grandfathered">{t("review.grandfathered")}</p>}
      {pending && <p className="break-words text-xs text-amber-200/90" data-testid="review-pending-note">{t(s.app_hidden ? "review.hiddenNew" : "review.liveKept")}</p>}
      {s.checks.length > 0 && <Checklist checks={s.checks} />}
      {s.checked_at && <p className="text-[11px] text-neutral-500" data-testid="review-checked-at">{t("review.checkedAt", { date: fmtDate(s.checked_at) })}</p>}
      <p className="break-words text-xs text-neutral-300" data-testid="review-audit">
        {s.audit ? t(s.audit.passed ? "review.auditOk" : "review.auditKo", { date: fmtDate(s.audit.at) }) : t("review.auditNone")}
        {s.audit?.note && <span className="text-neutral-400"> — {s.audit.note}</span>}
      </p>
      {s.published_at && <p className="text-xs text-neutral-300" data-testid="review-published-at">{t(s.forced ? "review.publishedForced" : "review.publishedAt", { date: fmtDate(s.published_at) })}</p>}
      <PushLine push={s.push} />
      {pending && (
        <div className="space-y-2 border-t border-neutral-800 pt-3">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={2} placeholder={t("review.notePh")}
            className="w-full min-w-0 rounded-xl border border-neutral-700 bg-[#0d0b0c] px-3 py-2 text-sm text-white focus:border-[var(--gold)] focus:outline-none" data-testid="review-note-input" />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-gold btn-sm" disabled={!!busy} onClick={() => act("audit", () => api.post(`/admin/apps/${appId}/audit`, { passed: true, note }))} data-testid="review-audit-ok-btn">
              {busy === "audit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("review.auditBtn")}
            </button>
            <button type="button" className="btn-red btn-sm" disabled={!!busy} onClick={() => act("run", () => api.post(`/admin/apps/${appId}/review/run`))} data-testid="review-rerun-btn">
              {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("review.rerun")}
            </button>
            <ConfirmButton title={t("review.forceTitle")} description={t("review.forceDesc")} onConfirm={() => act("force", () => api.post(`/admin/apps/${appId}/publish`, { confirm: true }))}
              className="btn-red btn-sm" testId="review-force-btn">
              {t("review.force")}
            </ConfirmButton>
          </div>
        </div>
      )}
    </section>
  );
};
